import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronDown, ChevronUp } from 'lucide-react';

const FilterSidebar = ({ selectedFilters, setSelectedFilters, onClose }) => {
  const [expandedSections, setExpandedSections] = React.useState({
    level: true,
    technology: true,
    industry: true,
    timeCommitment: true
  });

  const filterOptions = {
    level: [
      { value: 'all', label: 'All Levels' },
      { value: 'Beginner', label: 'Beginner' },
      { value: 'Intermediate', label: 'Intermediate' },
      { value: 'Advanced', label: 'Advanced' },
      { value: 'Expert', label: 'Expert' }
    ],
    technology: [
      { value: 'all', label: 'All Technologies' },
      { value: 'General', label: 'General Testing' },
      { value: 'Java', label: 'Java' },
      { value: 'Python', label: 'Python' },
      { value: 'JavaScript', label: 'JavaScript' },
      { value: 'API', label: 'API Testing' },
      { value: 'Playwright', label: 'Playwright' },
      { value: 'BDD', label: 'BDD/Cucumber' },
      { value: 'Advanced', label: 'Advanced Testing' }
    ],
    industry: [
      { value: 'all', label: 'All Industries' },
      { value: 'All Industries', label: 'General Purpose' },
      { value: 'Enterprise', label: 'Enterprise' },
      { value: 'Web Development', label: 'Web Development' },
      { value: 'Mobile Apps', label: 'Mobile Applications' },
      { value: 'Modern Web', label: 'Modern Web' },
      { value: 'Agile Teams', label: 'Agile Teams' }
    ],
    timeCommitment: [
      { value: 'all', label: 'Any Duration' },
      { value: 'short', label: '6-8 weeks' },
      { value: 'medium', label: '8-12 weeks' },
      { value: 'long', label: '12+ weeks' }
    ]
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearAllFilters = () => {
    setSelectedFilters({
      level: 'all',
      technology: 'all',
      industry: 'all',
      timeCommitment: 'all'
    });
  };

  const hasActiveFilters = Object.values(selectedFilters)?.some(filter => filter !== 'all');

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-card border border-border rounded-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Filters</h3>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Clear All
            </button>
          )}
          <button
            onClick={onClose}
            className="md:hidden text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Filter Sections */}
      <div className="space-y-6">
        {Object.entries(filterOptions)?.map(([filterType, options]) => (
          <div key={filterType} className="space-y-3">
            <button
              onClick={() => toggleSection(filterType)}
              className="flex items-center justify-between w-full text-left"
            >
              <h4 className="font-medium capitalize">
                {filterType === 'timeCommitment' ? 'Duration' : filterType}
              </h4>
              {expandedSections?.[filterType] ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {expandedSections?.[filterType] && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                {options?.map((option) => (
                  <label
                    key={option?.value}
                    className="flex items-center space-x-3 cursor-pointer group"
                  >
                    <input
                      type="radio"
                      name={filterType}
                      value={option?.value}
                      checked={selectedFilters?.[filterType] === option?.value}
                      onChange={() => handleFilterChange(filterType, option?.value)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm group-hover:text-blue-600 transition-colors">
                      {option?.label}
                    </span>
                  </label>
                ))}
              </motion.div>
            )}
          </div>
        ))}
      </div>
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="font-medium mb-3 text-sm">Active Filters:</h4>
          <div className="space-y-2">
            {Object.entries(selectedFilters)?.map(([type, value]) => {
              if (value === 'all') return null;
              
              const option = filterOptions?.[type]?.find(opt => opt?.value === value);
              return (
                <div key={type} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground capitalize">
                    {type === 'timeCommitment' ? 'Duration' : type}:
                  </span>
                  <span className="font-medium">{option?.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FilterSidebar;